import React from "react";
import { TrendingUp, Play, Pause, RotateCcw, Train, Sparkles, ArrowRightLeft, ChevronRight } from "lucide-react";
import { HORIZONS, LIVE_ASSUMPTIONS } from "../services/liveModel";

/* Live forecast and ingress/egress — §5 and §7.
 *
 * Leads with the clock and what is driving arrivals right now, because a
 * forecast without its cause is a number an operator cannot argue with.
 * "Ramkund reaches band D in 30 minutes" is useful; "…because the evening
 * aarti peak coincides with the 16:30 train cohort landing" is actionable.
 */
export default function ForecastPanel({
  lang = "en", live, running, speed, onRunningChange, onSpeedChange, onFocus,
}) {
  const L = (o) => o?.[lang] ?? o?.en ?? "";
  const fc = live.forecast();
  const deltas = live.deltas(30);
  const ie = live.ingressEgress();
  const acc = live.accuracy();
  const ctx = live.context;

  const hhmm = (min) => {
    const m = ((min % 1440) + 1440) % 1440;
    return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
  };

  const BAND_COLOR = { A: "#10B981", B: "#84CC16", C: "#F59E0B", D: "#F97316", E: "#DC2626", F: "#7F1D1D" };

  return (
    <div className="border-t border-slate-200">
      {/* Clock + transport */}
      <section className="px-3 py-3">
        <div className="flex items-center gap-1.5 mb-2">
          <TrendingUp size={12} className="text-slate-500" />
          <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
            {lang === "mr" ? "थेट अंदाज" : "Live Forecast"}
          </span>
          <span className="ml-auto text-[8px] font-black px-1.5 py-0.5 rounded border bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300">
            SIM
          </span>
        </div>

        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5">
          <span className="text-[15px] font-black tabular-nums text-slate-900">{hhmm(live.clock)}</span>
          <button
            type="button"
            onClick={() => onRunningChange(!running)}
            className="rounded border border-slate-300 bg-white p-1 text-slate-700 hover:bg-slate-100"
            aria-label={running ? "Pause" : "Play"}
          >
            {running ? <Pause size={11} /> : <Play size={11} />}
          </button>
          <button
            type="button"
            onClick={live.reset}
            className="rounded border border-slate-300 bg-white p-1 text-slate-700 hover:bg-slate-100"
            aria-label="Reset"
          >
            <RotateCcw size={11} />
          </button>
          <select
            value={speed}
            onChange={(e) => onSpeedChange(Number(e.target.value))}
            className="ml-auto rounded border border-slate-300 bg-white px-1 py-0.5 text-[10px] font-bold text-slate-700"
          >
            {[2, 5, 10, 30, 60].map((s) => (
              <option key={s} value={s}>{s}× </option>
            ))}
          </select>
        </div>

        {/* What is driving arrivals — the cause behind the curve. */}
        <div className="mt-1.5 space-y-1">
          {ctx.peak && (
            <div className="flex items-center gap-1.5 text-[10px] text-amber-800">
              <Sparkles size={10} className="flex-shrink-0" />
              <span className="font-bold">{L(ctx.peak.label)}</span>
              <span className="text-slate-500">
                ({Math.round(ctx.peak.weight * 100)}% {lang === "mr" ? "तीव्रता" : "intensity"})
              </span>
            </div>
          )}
          {ctx.nextArrival && (
            <div className="flex items-center gap-1.5 text-[10px] text-slate-600">
              <Train size={10} className="flex-shrink-0" />
              {ctx.nextArrival.people.toLocaleString("en-IN")}{" "}
              {lang === "mr" ? "प्रवासी" : "arriving"} · {ctx.nextArrival.sector} ·{" "}
              <span className="font-bold">T−{ctx.nextArrival.inMin}m</span>
            </div>
          )}
        </div>
      </section>

      {/* §5 — horizon grid */}
      <section className="px-3 py-3 border-t border-slate-200">
        <Head>{lang === "mr" ? "अंदाज क्षितिज" : "Forecast horizons"}</Head>
        <table className="w-full text-[9px]">
          <thead>
            <tr className="text-slate-400">
              <th className="text-left font-black">{lang === "mr" ? "झोन" : "Zone"}</th>
              {HORIZONS.map((h) => (
                <th key={h} className="text-right font-black tabular-nums">+{h}m</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {fc[HORIZONS[0]].map((row, i) => (
              <tr key={row.id} className="border-t border-slate-100">
                <td className="py-1 pr-1 text-slate-700 truncate max-w-[84px]">{L(row.name)}</td>
                {HORIZONS.map((h) => {
                  const cell = fc[h][i];
                  return (
                    <td key={h} className="py-1 text-right">
                      <button
                        type="button"
                        onClick={() => onFocus({ coord: cell.centre, zoom: 14 })}
                        className="inline-flex items-center gap-0.5 tabular-nums"
                        title={`${cell.current.toLocaleString("en-IN")} · ${cell.density}/m²`}
                      >
                        <span
                          className="inline-block h-2.5 w-2.5 rounded text-[7px] font-black text-white leading-[10px]"
                          style={{ background: BAND_COLOR[cell.band] }}
                        >
                          {cell.band}
                        </span>
                        <span className="text-slate-600">{cell.occupancyPct}%</span>
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        {deltas.length > 0 && (
          <div className="mt-2 rounded border border-orange-300 bg-orange-50 px-2 py-1.5">
            <div className="text-[9px] font-black uppercase tracking-wider text-orange-700">
              {lang === "mr" ? "30 मिनिटांत बदल" : "Band changes within 30 min"}
            </div>
            {deltas.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => onFocus({ coord: d.centre, zoom: 14.5 })}
                className="block text-left text-[10px] text-orange-900"
              >
                {L(d.name)}: {d.fromBand} → <strong>{d.band}</strong>{" "}
                <span className="text-orange-700">({d.direction})</span>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* §7 — ingress vs egress */}
      <section className="px-3 py-3 border-t border-slate-200">
        <div className="flex items-center gap-1.5 mb-2">
          <ArrowRightLeft size={11} className="text-slate-400" />
          <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
            {lang === "mr" ? "प्रवेश वि. निर्गमन" : "Ingress vs Egress"}
          </span>
        </div>
        <p className="mb-1.5 text-[9px] leading-snug text-slate-500">
          {lang === "mr"
            ? "आत येण्याचा दर बाहेर काढण्याच्या क्षमतेशी सुसंगत आहे का?"
            : "Is the rate we let people in compatible with our ability to get them out? A zone can look acceptable on occupancy and still be unsafe if the answer is no."}
        </p>
        {ie.slice(0, 4).map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => onFocus({ coord: r.centre, zoom: 14.5 })}
            className={`mb-1 block w-full rounded border p-1.5 text-left ${
              r.verdict === "unknown" ? "border-slate-300 bg-slate-100 border-dashed"
              : r.verdict === "critical" ? "border-red-300 bg-red-50"
              : r.verdict === "incompatible" ? "border-orange-300 bg-orange-50"
              : r.verdict === "tight" ? "border-amber-200 bg-amber-50"
              : "border-slate-200 bg-white"
            }`}
          >
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-bold text-slate-800 truncate flex-1">{L(r.name)}</span>
              <span className="text-[11px] font-black tabular-nums text-slate-900">
                {r.ratio == null ? "—" : `${r.ratio}×`}
              </span>
            </div>
            {r.ratio == null ? (
              /* A survey gap, shown as one. Colouring this green because no
                 number came back would be the most dangerous thing the
                 panel could do. */
              <div className="text-[9px] italic text-slate-500">
                {lang === "mr"
                  ? "या क्षेत्रासाठी निर्गमन द्वार नकाशात नाही — गुणोत्तर अज्ञात."
                  : "No exit gate mapped for this sector — ratio cannot be computed."}
              </div>
            ) : (
              <div className="text-[9px] text-slate-600 tabular-nums">
                {lang === "mr" ? "आत" : "in"} {r.inflowPerMin}/min ·{" "}
                {lang === "mr" ? "निर्गमन क्षमता" : "egress"} {r.egressCapacityPerMin}/min
                {r.minutesToClear != null && ` · ${lang === "mr" ? "रिकामे" : "clear"} ~${r.minutesToClear}m`}
              </div>
            )}
          </button>
        ))}
      </section>

      {/* §22 — prediction accuracy, measured not asserted */}
      <section className="px-3 py-3 border-t border-slate-200">
        <Head>{lang === "mr" ? "अंदाज अचूकता" : "Prediction accuracy"}</Head>
        {!acc.available ? (
          <p className="rounded border border-dashed border-slate-200 px-2 py-2 text-[10px] text-slate-500">
            {acc.reason}
          </p>
        ) : (
          <>
            <div className="flex flex-wrap gap-1.5">
              {acc.byHorizon.map((h) => (
                <span key={h.horizon} className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[9px] tabular-nums">
                  +{h.horizon}m <strong className="text-slate-900">{h.mape}%</strong>
                  <span className="text-slate-400"> MAPE · n={h.samples}</span>
                </span>
              ))}
            </div>
            <p className="mt-1 text-[9px] leading-snug text-slate-400">
              {lang === "mr"
                ? "मॉडेलची स्वतःच्या अंदाजाशी तुलना — वास्तविक मोजमापाशी नाही."
                : "Scored against what the model itself later produced, not against observed reality. It measures internal consistency, not real-world accuracy."}
            </p>
          </>
        )}
      </section>

      {/* Method */}
      <section className="px-3 pb-3">
        <details>
          <summary className="flex cursor-pointer items-center gap-0.5 text-[9px] font-bold text-slate-400 hover:text-slate-600">
            <ChevronRight size={8} />
            {lang === "mr" ? "मॉडेल गृहीतके" : "Model assumptions"}
          </summary>
          <ul className="mt-1 space-y-0.5">
            {LIVE_ASSUMPTIONS.map((a) => (
              <li key={a} className="text-[9px] leading-snug text-slate-500">· {a}</li>
            ))}
          </ul>
        </details>
      </section>
    </div>
  );
}

const Head = ({ children }) => (
  <div className="mb-2 text-[10px] font-black uppercase tracking-wider text-slate-400">{children}</div>
);
